import androidx.compose.runtime.Composable
import kotlinx.serialization.Serializable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Serializable
class Spell(
    val name: String,
    val description: String,
    val traits: List<String>,
    val category: String,
    val level: Int,
    val rarity: String,
    val duration: String,
    val castTime: String,
    val components: Components,
    val areaType: String,
    val areaSize: Int?,
    val range: String,
    val target: String,
    val source: String,
    val school: String,
    val cost: String,
    val traditions: List<String>
) {
    companion object {

        @Serializable
        data class Components(val material: Boolean, val somatic: Boolean, val verbal: Boolean) {
            fun toSet(): Set<String> {
                val list = mutableSetOf<String>()
                if (material) list.add("material")
                if (somatic) list.add("somatic")
                if (verbal) list.add("verbal")
                return list
            }
        }

        @Composable
        fun SpellView(spell: Spell) {
            Div({ style { padding(25.px) } }) {
                SpellTitleBar(spell)
                SpellFeatures(spell)
                Div {
                    Html(spell.description)
                }
            }
        }

        @Composable
        fun SpellTitleBar(spell: Spell) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    backgroundColor(Color.antiquewhite)
                    padding(10.px)
                }
            }) {
                Span {
                    Text(spell.name)
                }
                Span({
                    style {
                        alignContent(AlignContent.End)
                    }
                }) {
                    Text(categoryText(spell))
                }
            }
        }

        private fun categoryText(spell: Spell): String {
            val parts = mutableListOf<String>()
            if (spell.category == "focus") parts.add("Focus")
            if (spell.traits.contains("cantrip")) parts.add("Cantrip")
            if (parts.isEmpty()) parts.add("Spell")
            parts.add(spell.level.toString())
            return parts.joinToString(" ")
        }

        @Composable
        fun SpellTraits(spell: Spell) {
            Div {
                when (spell.rarity) {
                    "uncommon" -> Trait("uncommon", Rarity.UNCOMMON)
                    "rare" -> Trait("rare", Rarity.RARE)
                }
                for (i in spell.traits) {
                    Trait(i, Rarity.COMMON)
                }
            }

        }

        @Composable
        fun Trait(name: String, rarity: Rarity) {
            Span({
                style {
                    when (rarity) {
                        Rarity.COMMON -> backgroundColor(Color("#5e0000"))
                        Rarity.UNCOMMON -> backgroundColor(Color("#c45500"))
                        Rarity.RARE -> backgroundColor(Color("#0c1466"))
                    }
                    border {
                        width(2.px)
                        color(Color("#dcb07b"))
                        style(LineStyle.Solid)
                    }
                    paddingLeft(6.px)
                    paddingRight(6.px)
                    paddingBottom(3.px)
                    paddingTop(3.px)
                    color(Color.white)
                }
            }) {
                Text(name)
            }
        }

        fun componentsString(components: Components): String {
            val parts = mutableListOf<String>()
            if (components.material) parts.add("material")
            if (components.somatic) parts.add("somatic")
            if (components.verbal) parts.add("verbal")
            return parts.joinToString(", ")
        }

        @Composable
        fun SpellFeatures(spell: Spell) {
            Div {

                Div({ style { margin(10.px) } }) {
                    SpellTraits(spell)
                }
                if (spell.duration.isNotBlank()) {
                    Div({ style { margin(10.px) } }) {
                        Span({ style { fontWeight("bold") } }) { Text("Duration: ") }
                        Span { Text(spell.duration) }
                    }
                }
                Div({ style { margin(10.px) } }) {
                    Span({ style { fontWeight("bold") } }) { Text("Cast: ") }
                    Span {
                        CastTime(spell.castTime)
                    }
                    Span({ style { marginLeft(5.px) } }) { Text(componentsString(spell.components)) }
                    if (spell.cost.isNotBlank()) {
                        Property("Cost", spell.cost)
                    }
                }
                Div({ style { margin(10.px) } }) {
                    Text(spell.traditions.joinToString(", "))
                }
                Div({ style { margin(10.px) } }) {
                    if (spell.target.isNotBlank())
                        Property("Target", spell.target)
                    if (spell.areaSize != null)
                        Property("Area", spell.areaSize.toString() + "-foot " + spell.areaType)
                    if (spell.range.isNotBlank())
                        Property("Range", spell.range)
                    Property("Source", spell.source)
                    Property("School", spell.school)
                }
            }
        }
    }

    enum class Rarity {
        COMMON, UNCOMMON, RARE
    }
}

@Composable
fun Property(name: String, value: String) {
    Div {
        Span({ style { fontWeight("bold") } }) {
            Text(name + ": ")
        }
        Text(value)
    }
}

@Composable
fun CastTime(value: String) {
    val orComponents = value.split("or")
    orComponents.forEachIndexed { orIndex, orComponent ->
        val toComponents = orComponent.split("to")
        toComponents.forEachIndexed { toIndex, toComponent ->
            when (toComponent.trim()) {
                "1" -> Img("1action.png", attrs = { style { height(1.em) } })
                "2" -> Img("2action.png", attrs = { style { height(1.em) } })
                "3" -> Img("3action.png", attrs = { style { height(1.em) } })
                "reaction" -> Img("reaction.png", attrs = { style { height(1.em) } })
                "free" -> Img("freeaction.png", attrs = { style { height(1.em) } })
                else -> Text(toComponent)
            }
            if (toIndex < toComponents.size - 1) {
                Text(" to ")
            }
        }
        if (orIndex < orComponents.size - 1) {
            Text(" or ")
        }
    }

}